import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Skeleton } from 'antd';
import { get } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faCar,
  faChevronLeft,
  faChevronRight,
  faCircleExclamation,
  faHotel,
  faPersonHiking,
  faPlaneDeparture,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';

import { IReservation } from '@share/common-types';
import { ReservationsAPI } from '@share/api';

import { parseApiDate } from '../../utils';
import { AccountEmptyState } from '../empty-state';

import styles from './styles.module.scss';

interface IProps {
  /** `horizontal` (photo beside the details) when the card spans the full width. */
  layout?: 'vertical' | 'horizontal';
  trips: IReservation[];
  loading: boolean;
  failed: boolean;
  bookingsPath: string | null;
  planPath: string;
  /** Travel agents book for clients: title each trip with its lead guest ("Maria Lopez's next trip"). */
  showLeadName?: boolean;
}

const getProductKey = (trip: IReservation): string => (trip.bookingType || '').toLowerCase();

const PRODUCT_ICONS: Record<string, IconDefinition> = {
  hotel: faHotel,
  condo: faBuilding,
  car: faCar,
  activity: faPersonHiking,
};

/** Car and activity bookings carry their own picture; hotels and condos need the property content. */
const getInlineImage = (trip: IReservation): string | null => {
  const product = getProductKey(trip);
  if (product === 'car') {
    return get(trip, 'bookingUserCar.vehicle.pictureURL', null);
  }
  if (product === 'activity') {
    return get(trip, 'bookingUserActivity.titleImage', null);
  }
  return null;
};

const getTripKey = (trip: IReservation): string => trip.bookingId || String(trip.bookingInternalId);

const getDaysUntil = (value: string | null | undefined): number | null => {
  const date = parseApiDate(value);
  if (!date) {
    return null;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((date.getTime() - today.getTime()) / 86400000);
};

// `cashbackEarned` / `totalPaid` arrive already formatted ("$12.50"); only show amounts above zero.
const hasAmount = (value: string | null | undefined): boolean => Number((value || '').replace(/[^0-9.]/g, '')) > 0;

export const NextTrips = ({ layout = 'vertical', trips, loading, failed, bookingsPath, planPath, showLeadName = false }: IProps): React.ReactElement => {
  const intl = useIntl();
  const [index, setIndex] = useState(0);
  const [images, setImages] = useState<Record<string, string | null>>({});
  const requested = useRef<Set<string>>(new Set());

  const trip = trips[index] || null;

  useEffect(() => {
    setIndex(0);
  }, [trips]);

  // Load the property image for the trip on screen only, once per trip.
  useEffect(() => {
    if (!trip) {
      return;
    }
    const key = getTripKey(trip);
    if (requested.current.has(key)) {
      return;
    }
    requested.current.add(key);

    const inline = getInlineImage(trip);
    if (inline || !trip.propertyId) {
      setImages(current => ({ ...current, [key]: inline }));
      return;
    }

    let isActive = true;
    ReservationsAPI.getStatic(trip.propertyId)
      .then(({ data }) => {
        if (isActive) {
          setImages(current => ({ ...current, [key]: data?.imageUrl || null }));
        }
      })
      .catch((error: unknown) => {
        console.error('Account overview: trip image request failed', error);
        if (isActive) {
          setImages(current => ({ ...current, [key]: null }));
        }
      });

    return () => {
      isActive = false;
    };
  }, [trip]);

  const formatDate = (value: string | null | undefined): string => {
    const date = parseApiDate(value);
    return date ? intl.formatDate(date, { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  };

  const renderBody = () => {
    if (loading) {
      return <div className={styles.tripState}><Skeleton active paragraph={{ rows: 4 }} title={false} /></div>;
    }
    if (failed) {
      return (
        <div className={styles.tripState}>
          <AccountEmptyState
            icon={faCircleExclamation}
            title={intl.formatMessage({ id: 'my.account.hub.next_trip.error_title' })}
            body={intl.formatMessage({ id: 'my.account.hub.next_trip.error' })}
          />
        </div>
      );
    }
    if (!trip) {
      return (
        <div className={styles.tripState}>
          <AccountEmptyState
            icon={faPlaneDeparture}
            title={intl.formatMessage({ id: 'my.account.hub.next_trip.empty' })}
            body={intl.formatMessage({ id: 'my.account.hub.next_trip.empty_body' })}
            action={{ label: intl.formatMessage({ id: 'my.account.hub.next_trip.plan' }), to: planPath }}
          />
        </div>
      );
    }

    const key = getTripKey(trip);
    const image = images[key];
    const isCar = getProductKey(trip) === 'car';
    const productIcon = PRODUCT_ICONS[getProductKey(trip)] || faPlaneDeparture;
    const daysUntil = getDaysUntil(trip.checkIn);
    const totalPaid = trip.priceDetail?.totalPaid;
    const cashback = trip.priceDetail?.cashbackEarned;

    const facts: { label: string; value: string | number }[] = [
      { label: intl.formatMessage({ id: isCar ? 'cars.pickup' : 'my.account.hub.next_trip.check_in' }), value: formatDate(trip.checkIn) },
      { label: intl.formatMessage({ id: isCar ? 'cars.dropoff' : 'check.out' }), value: formatDate(trip.checkOut) },
    ];
    if (!isCar && trip.numberOfNights) {
      facts.push({ label: intl.formatMessage({ id: 'my.account.hub.next_trip.nights' }), value: trip.numberOfNights });
    }
    if (!isCar && trip.numberGuest) {
      facts.push({
        label: intl.formatMessage({ id: 'my.account.hub.next_trip.guests' }),
        value: intl.formatMessage({ id: 'my.account.hub.next_trip.guests_value' }, { guests: trip.numberGuest, rooms: trip.roomsCount || 1 }),
      });
    }
    if (trip.bookingId) {
      facts.push({ label: intl.formatMessage({ id: 'my.account.hub.next_trip.confirmation' }), value: trip.bookingId });
    }
    if (hasAmount(totalPaid)) {
      facts.push({ label: intl.formatMessage({ id: 'my.account.hub.next_trip.total' }), value: totalPaid as string });
    }

    return (
      <div className={styles.tripSlide} aria-live="polite">
        <div className={styles.tripMedia}>
          {image ? (
            <img src={image} alt="" className={styles.tripImage} />
          ) : (
            <div className={styles.tripPlaceholder} aria-hidden="true">
              <FontAwesomeIcon icon={productIcon} />
            </div>
          )}
          {trip.bookingType ? <span className={styles.tripType}>{trip.bookingType}</span> : null}
          {daysUntil !== null && daysUntil >= 0 ? (
            <span className={styles.tripCountdown}>
              {intl.formatMessage({ id: 'my.account.hub.next_trip.countdown' }, { days: daysUntil })}
            </span>
          ) : null}
        </div>
        <div className={styles.trip}>
          <div className={styles.tripTitleRow}>
            <div className={styles.tripName}>{trip.propertyName}</div>
            {typeof trip.refundable === 'boolean' ? (
              <span className={`${styles.tripStatus} ${styles[trip.refundable ? 'tripStatus_ok' : 'tripStatus_neutral']}`}>
                {intl.formatMessage({ id: trip.refundable ? 'refundable' : 'non.refundable' })}
              </span>
            ) : null}
          </div>
          {trip.propertyCity ? <div className={styles.muted}>{trip.propertyCity}</div> : null}
          <dl className={styles.tripFactsGrid}>
            {facts.map(fact => (
              <div key={fact.label}>
                <dt>{fact.label}</dt>
                <dd>{fact.value}</dd>
              </div>
            ))}
          </dl>
          {hasAmount(cashback) ? (
            <div className={styles.tripTags}>
              <span className={styles.tripTagEarn}>
                {intl.formatMessage({ id: 'my.account.hub.next_trip.earns' }, { amount: cashback })}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const hasMany = trips.length > 1;

  return (
    <section
      className={`${styles.card} ${styles.nextTrips} ${layout === 'horizontal' ? styles.nextTripsHorizontal : ''}`} aria-labelledby="account-hub-next-trip-title" aria-roledescription="carousel">
      <div className={styles.cardHeader}>
        <span id="account-hub-next-trip-title" className={styles.label}>
          {showLeadName && trip?.leadName?.trim()
            ? intl.formatMessage({ id: 'my.account.hub.next_trip.title_for' }, { name: trip.leadName.trim() })
            : intl.formatMessage({ id: 'my.account.hub.next_trip.title' })}
        </span>
        {hasMany ? (
          <span className={styles.label}>
            {intl.formatMessage({ id: 'my.account.hub.next_trip.position' }, { current: index + 1, total: trips.length })}
          </span>
        ) : null}
      </div>

      {renderBody()}

      {trip && !loading && !failed ? (
        <div className={styles.tripFooter}>
          {bookingsPath ? (
            <Link to={bookingsPath} className={styles.secondaryButton}>
              {intl.formatMessage({ id: 'my.account.hub.next_trip.view' })}
            </Link>
          ) : <span />}
          {hasMany ? (
            <div className={styles.carouselControls}>
              <button
                type="button"
                className={styles.carouselArrow}
                onClick={() => setIndex(current => (current - 1 + trips.length) % trips.length)}
                aria-label={intl.formatMessage({ id: 'my.account.hub.next_trip.previous' })}
              >
                <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
              </button>
              <div className={styles.carouselDots}>
                {trips.map((item, dotIndex) => (
                  <button
                    key={getTripKey(item)}
                    type="button"
                    className={dotIndex === index ? `${styles.carouselDot} ${styles.carouselDotActive}` : styles.carouselDot}
                    onClick={() => setIndex(dotIndex)}
                    aria-label={intl.formatMessage({ id: 'my.account.hub.next_trip.go_to' }, { current: dotIndex + 1, total: trips.length })}
                    aria-current={dotIndex === index ? 'true' : undefined}
                  />
                ))}
              </div>
              <button
                type="button"
                className={styles.carouselArrow}
                onClick={() => setIndex(current => (current + 1) % trips.length)}
                aria-label={intl.formatMessage({ id: 'my.account.hub.next_trip.next' })}
              >
                <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};
